import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Document, Packer, Paragraph, Table, TableCell, TableRow } from "docx";
// import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
// import { jsPDF } from "jspdf";
import "jspdf-autotable";
// import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
// import TableChartIcon from "@mui/icons-material/TableChart";
import { useDispatch, useSelector } from "react-redux";
import { setFilteredReports } from "../../../../../store/slices/filteredDataSlice";
import { fetchAllProjects } from "../../../../../store/slices/projectsSlice";
import { fetchCompanyProfiles } from "../../../../../store/slices/companyProfileSlice";
import { fetchAllQuotations } from "../../../../../store/slices/generateQuotationSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import { ChartColumnDecreasing } from "lucide-react"
import { TableOfContents } from "lucide-react"
import { Download } from "lucide-react"
import pdfMake from "pdfmake/build/pdfmake.min";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { isWonLeadStatus } from "../../../../../utils/syncLeadProjectBucketStatus";
import { shouldShowLeadOnActualPl } from "../../../../../utils/hasSubmittedProjectFromOngoing";
import { getLeadServicePricesFromLatestQuotation, aggregateCrmRevenueFromLatestQuotations } from "../../../../../utils/crmRevenueQuotationPrices";

pdfMake.vfs = pdfFonts.vfs;

const getRevenueLeadRowKey = (row) =>
    row?.quotationId != null && String(row.quotationId).trim() !== ""
        ? String(row.quotationId).trim()
        : String(row?.ProjectName ?? "").trim();


const Sales = ({ showgraph, filteredReports = [], isDownloadViewOnly = false }) => {
    const popupRef = useRef(null);
    const revenueMousePos = useRef({ x: 0, y: 0 });
    const revenueChartRef = useRef(null);
    const [isRevenueChartHovered, setIsRevenueChartHovered] = useState(false);
    const currency = useCurrencySymbol();
    const dispatch = useDispatch();
    const USAComma = CommaConvert();
    const { projects } = useSelector((state) => state.projects);
    const { profiles } = useSelector((state) => state.companyProfile);
    const { quotations } = useSelector((state) => state.generateQuotation);

    const pkgNormalized = String(profiles?.[0]?.subscriptionpackage || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    const isErpPackage = pkgNormalized.includes("erp");
    // Align with Sales P&L: CRM when package is CRM / non-ERP (ERP keeps Actual Revenue path).
    const isCrmPackage =
        !isErpPackage &&
        (pkgNormalized === "crm" ||
            pkgNormalized.includes("crm") ||
            pkgNormalized.length === 0 ||
            (pkgNormalized.length > 0 && !pkgNormalized.includes("erp")));

    const [topLeads, setTopLeads] = useState([]); // New state for top 10 leads
    const [groupFilteredReports, setGroupFilteredReports] = useState([]);

    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [probabilityTab, setProbabilityTab] = useState("graph");
    const [isDropdownVisibleRevenue, setIsDropdownVisibleRevenue] =
        useState(false);
    const [probabilityTabRevenue, setProbabilityTabRevenue] = useState("graph");
    const [selectedRevenueYear, setSelectedRevenueYear] = useState("All Years");

    useEffect(() => {
        if (filteredReports && Array.isArray(filteredReports)) {

            const wonLeads = filteredReports.filter((lead) =>
                isWonLeadStatus(lead.Status)
            );
            setGroupFilteredReports(wonLeads);
        }
    }, [filteredReports]);

    const getLeadYearValue = (lead) => {
        const dateRaw = lead?.LeadDate ?? lead?.leadDate ?? lead?.createdAt ?? lead?.createdOn ?? null;
        if (!dateRaw) return null;
        const parsed = new Date(dateRaw);
        if (Number.isNaN(parsed.getTime())) return null;
        return String(parsed.getFullYear());
    };

    const revenueYearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, index) => String(currentYear - index));
    }, []);

    const revenueFilteredReports = useMemo(() => {
        if (selectedRevenueYear === "All Years") {
            return groupFilteredReports;
        }
        return (groupFilteredReports || []).filter(
            (lead) => getLeadYearValue(lead) === selectedRevenueYear
        );
    }, [groupFilteredReports, selectedRevenueYear]);

    useEffect(() => {
        dispatch(setFilteredReports());
    }, [dispatch]);

    useEffect(() => {
        if (!Array.isArray(projects) || projects.length === 0) {
            dispatch(fetchAllProjects());
        }
    }, [dispatch, projects]);

    useEffect(() => {
        if (!Array.isArray(profiles) || profiles.length === 0) {
            dispatch(fetchCompanyProfiles());
        }
    }, [dispatch, profiles]);

    // CRM Revenue: need GeneratedQuote Finance Summary prices (latest version per lead).
    useEffect(() => {
        if (!isCrmPackage) return;
        dispatch(fetchAllQuotations());
    }, [dispatch, isCrmPackage]);

    useEffect(() => {
        if (!isRevenueChartHovered) return undefined;

        const hideRevenueTooltip = () => setIsRevenueChartHovered(false);

        window.addEventListener("scroll", hideRevenueTooltip, true);
        window.addEventListener("wheel", hideRevenueTooltip, { passive: true, capture: true });

        return () => {
            window.removeEventListener("scroll", hideRevenueTooltip, true);
            window.removeEventListener("wheel", hideRevenueTooltip, { capture: true });
        };
    }, [isRevenueChartHovered]);

    useEffect(() => {
        if (groupFilteredReports && Array.isArray(groupFilteredReports)) {
            // Consolidate same client names (sum QuotedValue), then take Top 10
            const consolidatedByClient = Object.values(
                [...groupFilteredReports]
                    .filter((lead) => Number.isFinite(Number(lead.QuotedValue)))
                    .reduce((acc, lead) => {
                        const key = String(lead.ClientName || "")
                            .trim()
                            .toLowerCase();
                        if (!key) return acc;
                        const quoted = Number(lead.QuotedValue) || 0;
                        if (!acc[key]) {
                            acc[key] = {
                                ...lead,
                                ClientName: String(lead.ClientName || "").trim(),
                                QuotedValue: quoted,
                            };
                        } else {
                            acc[key].QuotedValue += quoted;
                        }
                        return acc;
                    }, {})
            );

            const top10SortedLeads = consolidatedByClient
                .sort((a, b) => b.QuotedValue - a.QuotedValue)
                .slice(0, 10)
                .sort((a, b) => a.QuotedValue - b.QuotedValue);

            setTopLeads(top10SortedLeads);
        } else {
            setTopLeads([]);
        }
    }, [groupFilteredReports]);

    const handleClickOutside = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setIsDropdownVisibleRevenue(false); // Close the popup if clicked outside
        }
    };
    useEffect(() => {
        if (isDropdownVisibleRevenue) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownVisibleRevenue]);

    const dropdownRefclient = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRefclient.current &&
                !dropdownRefclient.current.contains(event.target)
            ) {
                setIsDropdownVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const toggleDropdown = () => {
        if (isDownloadViewOnly) return;
        setIsDropdownVisible(!isDropdownVisible);
    };
    const handleDownload = (event) => {
        if (isDownloadViewOnly) return;
        const value = event.target.value;

        if (value === "pdf") {
            downloadPDF();
        } else if (value === "word") {
            downloadWord();
        } else if (value === "excel") {
            downloadExcel();
        }
        setIsDropdownVisible(false);
    };

    const headerColor = "#0e7490"

    const downloadPDF = () => {

        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" });
        const year = now.getFullYear();
        const fileName = `ClientLeadReports_${day}_${month}_${year}.pdf`;

        // 👉 Instead of topLeads from jsPDF, we use tableLeadReports here
        const totalQuotedValue = topLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue || 0),
            0
        );

        const formattedTotal = `${USAComma(totalQuotedValue)}`;

        // ------------ HEADER ROW ------------
        const headerRow = [
            { text: "Sr.No", style: "tableHeader", fillColor: headerColor, color: "white" },
            { text: "Client Name", style: "tableHeader", fillColor: headerColor, color: "white" },
            { text: `Quoted Value (${currency})`, style: "tableHeader", fillColor: headerColor, color: "white" }
        ];

        // ------------ BODY ROWS ------------
        const bodyRows = topLeads.map((LeadReport, index) => {
            const isEvenRow = index % 2 === 0;
            const bgColor = isEvenRow ? "#ffffff" : "#f3f4f6";

            return [
                { text: index + 1, fillColor: bgColor, style: "tableBody" },
                { text: LeadReport.ClientName, fillColor: bgColor, style: "tableBody" },
                { text: `${USAComma(LeadReport.QuotedValue)}`, fillColor: bgColor, style: "tableBody" }
            ];
        });

        // ------------ FOOTER ROW (TOTAL) ------------
        const footerRow = [
            { text: " ", style: "tableBody", fillColor: "#e5e7eb" },
            { text: "Total", bold: true, fillColor: "#e5e7eb", style: "tableBody" },
            { text: formattedTotal, bold: true, fillColor: "#e5e7eb", style: "tableBody" }
        ];

        // ------------ PDF DOCUMENT DEFINITION ------------
        const docDefinition = {
            pageSize: "A4",
            pageOrientation: "portrait",

            content: [
                {
                    text: "ClientLeadReports",
                    style: "title",
                    margin: [0, 0, 0, 10]
                },
                {
                    table: {
                        headerRows: 1,
                        widths: [40, "*", 80],
                        body: [
                            headerRow,
                            ...bodyRows,
                            footerRow        // 👈 Add total row like jsPDF
                        ]
                    },
                    layout: "lightHorizontalLines"
                }
            ],

            styles: {
                title: { fontSize: 12, bold: true },
                tableHeader: { bold: true, fontSize: 10 },
                tableBody: { fontSize: 9 }
            }
        };

        pdfMake.createPdf(docDefinition).download(fileName);
    };

    const downloadWord = () => {
        const headerRow = new TableRow({
            children: [
                new TableCell({ children: [new Paragraph("Sr.No")] }),

                new TableCell({ children: [new Paragraph("Client Name")] }),
                new TableCell({ children: [new Paragraph(`Quoted Value${currency}`)] }),
            ],
        });

        const dataRows = topLeads.map((LeadReport, index) => {
            return new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(`${index + 1}`)] }),
                    new TableCell({ children: [new Paragraph(LeadReport.ClientName)] }),
                    new TableCell({
                        children: [
                            new Paragraph(
                                ` ${USAComma(LeadReport.QuotedValue)}`
                            ),
                        ],
                    }),
                ],
            });
        });

        // 🔢 Calculate total quoted value
        const totalQuotedValue = topLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );
        const formattedTotal = ` ${USAComma(totalQuotedValue)}`;

        // ➕ Footer Row
        const footerRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph("")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph("Total")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph(formattedTotal)],
                    verticalAlign: "center",
                }),
            ],
        });

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        new Table({
                            rows: [headerRow, ...dataRows, footerRow],
                        }),
                    ],
                },
            ],
        });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, "ClientLeadReports.docx");
        });
    };

    const downloadExcel = () => {
        const sortedLeads = [...topLeads].sort(
            (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
        );

        const quotedValueKey = `Quoted Value (${currency})`;

        // Create data rows
        const data = sortedLeads.map((LeadReport, index) => ({
            "Sr.No": index + 1,
            "Client Name": LeadReport.ClientName,
            [quotedValueKey]: `${USAComma(Number(LeadReport.QuotedValue))}`,
        }));

        // Calculate total
        const totalQuotedValue = sortedLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );

        // Add total row
        data.push({
            "Sr.No": "",
            "Client Name": "Total",
            [quotedValueKey]: `${USAComma(totalQuotedValue)}`,
        });

        // Generate worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        // Save as Excel file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "ClientLeadReports.xlsx");
    };


    // const AllClient = () => {
    //     const sortedLeads = [...groupFilteredReports].sort(
    //         (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
    //     );

    //     // Create data rows
    //     const data = sortedLeads.map((LeadReport, index) => ({
    //         "Sr.No": index + 1,
    //         "Client Name": LeadReport.ClientName,
    //         "Quoted Value": Number(LeadReport.QuotedValue), // Keep as number
    //     }));

    //     // ➕ Calculate total
    //     const totalQuotedValue = sortedLeads
    //         .reduce((sum, lead) => sum + Number(lead.QuotedValue), 0);

    //     // Add a total row at the bottom
    //     data.push({
    //         "Sr.No": "",
    //         "Client Name": "Total",
    //         "Quoted Value": USAComma(totalQuotedValue),
    //     });

    //     // Generate worksheet and workbook
    //     const ws = XLSX.utils.json_to_sheet(data);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

    //     // Save as Excel file
    //     const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    //     const excelFile = new Blob([excelBuffer], {
    //         bookType: "xlsx",
    //         type: "application/octet-stream",
    //     });
    //     saveAs(excelFile, "LeadReports.xlsx");
    // };

    const AllClient = () => {
        if (isDownloadViewOnly) return;
        const sortedLeads = [...groupFilteredReports].sort(
            (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
        );

        const quotedValueKey = `Quoted Value (${currency})`;

        // Create data rows
        const data = sortedLeads.map((LeadReport, index) => ({
            "Sr.No": index + 1,
            "Client Name": LeadReport.ClientName,
            [quotedValueKey]: `${USAComma(Number(LeadReport.QuotedValue))}`, // formatted
        }));

        // Calculate total
        const totalQuotedValue = sortedLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );

        // Add total row
        data.push({
            "Sr.No": "",
            "Client Name": "Total",
            [quotedValueKey]: `${USAComma(totalQuotedValue)}`, // same key
        });

        // Generate worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        // Save as Excel file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "AllClientList.xlsx");
    };

    const AllClientServiceType = () => {
        if (isDownloadViewOnly) return;
        if (!groupFilteredReports || !Array.isArray(groupFilteredReports)) return;

        const amountKey = `QuotedValue (${currency})`;

        // Prepare data with only required columns
        const data = groupFilteredReports.map((lead) => ({
            TypeService: lead.TypeService?.join(", ") || "", // join array into string
            [amountKey]: `${USAComma(Number(lead.QuotedValue))}`,
        }));

        const totalAmount = groupFilteredReports.reduce(
            (sum, lead) => sum + (Number(lead.QuotedValue) || 0),
            0
        );

        data.push({
            TypeService: "Total",
            [amountKey]: `${USAComma(totalAmount)}`,
        });

        // Convert to worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

        // Generate Excel file and trigger download
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "ClientServicesList.xlsx");
    };
    //for Revenue//
    const toggleDropdownRevenue = () => {
        if (isDownloadViewOnly) return;
        setIsDropdownVisibleRevenue(!isDropdownVisibleRevenue);
    };

    const handleDownloadRevenue = (event) => {
        if (isDownloadViewOnly) return;
        const value = event.target.value;

        if (value === "pdf") {
            downloadPDFRevenue();
        } else if (value === "word") {
            downloadWordRevenue();
        } else if (value === "excel") {
            downloadExcelRevenue();
        }
        setIsDropdownVisibleRevenue(false);
    };

    const downloadPDFRevenue = () => {
        const revenueRows = [...(datarevenue || [])]
            .filter((item) => Number(item.originalValue || 0) > 0)
            .sort((a, b) => Number(b.originalValue || 0) - Number(a.originalValue || 0));

        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" });
        const year = now.getFullYear();
        const fileName = `RevenueLeadReport_${day}_${month}_${year}.pdf`;

        const totalAmount = revenueRows.reduce(
            (sum, row) => sum + Number(row.originalValue || 0),
            0
        );
        const formattedTotal = `${USAComma(totalAmount)}`;

        const headerRow = [
            { text: "Sr.No", style: "tableHeader" },
            { text: "Service Type", style: "tableHeader" },
            { text: `Amount (${currency})`, style: "tableHeader" }
        ];

        const bodyRows = revenueRows.map((row, index) => {
            const isEven = index % 2 === 0;
            const bg = isEven ? "#ffffff" : "#f3f4f6";

            return [
                { text: index + 1, fillColor: bg, style: "tableBody" },
                { text: row.name || "", fillColor: bg, style: "tableBody" },
                { text: `${USAComma(row.originalValue)}`, fillColor: bg, style: "tableBody" }
            ];
        });

        const footerRow = [
            { text: " ", fillColor: "#e5e7eb", style: "tableBody" },
            { text: "Total", bold: true, fillColor: "#e5e7eb", style: "tableBody" },
            { text: formattedTotal, bold: true, fillColor: "#e5e7eb", style: "tableBody" }
        ];

        const docDefinition = {
            pageSize: "A4",
            pageOrientation: "portrait",

            content: [
                {
                    text: "Revenue Lead Report",
                    style: "title",
                    margin: [0, 0, 0, 10]
                },
                {
                    table: {
                        headerRows: 1,
                        widths: [40, "*", 80],
                        body: [headerRow, ...bodyRows, footerRow]
                    },
                    layout: {
                        fillColor: (rowIndex) => {
                            if (rowIndex === 0) return headerColor;
                            return null;
                        },
                        hLineColor: () => "#e5e7eb",
                        vLineColor: () => "#e5e7eb"
                    }
                }
            ],

            styles: {
                title: { fontSize: 12, bold: true },
                tableHeader: { bold: true, fontSize: 10, color: "white" },
                tableBody: { fontSize: 9 }
            }
        };

        pdfMake.createPdf(docDefinition).download(fileName);
    };


    const downloadWordRevenue = () => {
        const revenueRows = [...(datarevenue || [])]
            .filter((item) => Number(item.originalValue || 0) > 0)
            .sort((a, b) => Number(b.originalValue || 0) - Number(a.originalValue || 0));

        const headerRow = new TableRow({
            children: [
                new TableCell({ children: [new Paragraph("S.No")] }),
                new TableCell({ children: [new Paragraph("Service Type")] }),
                new TableCell({ children: [new Paragraph(`Amount (${currency})`)] }),
            ],
        });

        const dataRows = revenueRows.map((row, index) => {
            return new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(`${index + 1}`)] }),
                    new TableCell({ children: [new Paragraph(row.name || "")] }),
                    new TableCell({
                        children: [
                            new Paragraph(`${USAComma(row.originalValue)}`),
                        ],
                    }),
                ],
            });
        });

        const totalAmount = revenueRows.reduce(
            (sum, row) => sum + Number(row.originalValue || 0),
            0
        );
        const formattedTotal = `${USAComma(totalAmount)}`;

        const footerRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph(" ")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph("Total")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph(formattedTotal)],
                    verticalAlign: "center",
                }),
            ],
        });

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        new Table({
                            rows: [headerRow, ...dataRows, footerRow],
                        }),
                    ],
                },
            ],
        });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, "RevenueLeadReport.docx");
        });
    };

    const downloadExcelRevenue = () => {
        const amountKey = `Amount (${currency})`;
        const revenueRows = [...(datarevenue || [])]
            .filter((item) => Number(item.originalValue || 0) > 0)
            .sort((a, b) => Number(b.originalValue || 0) - Number(a.originalValue || 0));

        const data = revenueRows.map((row, index) => ({
            "Sr. No": index + 1,
            "Service Type": row.name || "",
            [amountKey]: `${USAComma(Number(row.originalValue || 0))}`,
        }));

        const totalAmount = revenueRows.reduce(
            (sum, row) => sum + Number(row.originalValue || 0),
            0
        );

        data.push({
            "Sr. No": "",
            "Service Type": "Total",
            [amountKey]: `${USAComma(Number(totalAmount))}`,
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "RevenueLeadReport.xlsx");
    };


    // Rank-based donut colors (shared mapping) — matches provided reference.
    // Rank 1 → Dark green, Rank 2 → Light green, Rank 3 → Strong blue,
    // Rank 4 → Medium blue, Rank 5–10 → Light blue gradient.
    const COLORS = [
        "#1F7A3A",
        "#8DB84A",
        "#1A73E8",
        "#4A9ED9",
        "#74B5E8",
        "#9EC7F6",
        "#B8DCF5",
        "#CCE7F8",
        "#DDF0FA",
        "#EEF7FD",
    ];
    const COLORSrevenue = COLORS;

    const MAIN_REVENUE_SERVICE_TYPES = ["Software", "Hardware", "Cloud", "Installation"];
    const REVENUE_SERVICE_CATEGORIES = [...MAIN_REVENUE_SERVICE_TYPES, "Multiple"];

    const matchMainRevenueServiceType = (type) =>
        MAIN_REVENUE_SERVICE_TYPES.find(
            (mainType) => mainType.toLowerCase() === String(type || "").trim().toLowerCase()
        ) || null;

    const getLeadProjectsForRevenue = (lead) => {
        if (!Array.isArray(projects) || projects.length === 0) return [];

        const rowKey = getRevenueLeadRowKey(lead);
        const projectName = String(lead?.ProjectName || "").trim();

        // Prefer tracking projects (same set Actual P&L uses for visibility / salesperson path).
        const matched = projects.filter((proj) => {
            const projectKey =
                proj?.quotationId != null && String(proj.quotationId).trim() !== ""
                    ? String(proj.quotationId).trim()
                    : String(proj?.ProjectName ?? "").trim();

            if (rowKey && projectKey && projectKey === rowKey) return true;
            if (
                projectName &&
                String(proj?.ProjectName ?? "").trim() === projectName
            ) {
                return true;
            }
            return false;
        });

        const tracking = matched.filter((proj) => proj?.isProjecttracking === true);
        return tracking.length > 0 ? tracking : matched;
    };

    // ERP / Actual Itemized: Quoted Price per service = lead.QuotedValue × (Expected Margin % / 100).
    // Expected Margin comes from Quoted tab Salesmargin (prefer non-tracking twin when tracking has none).
    // Falls back to equal-split only when no margins exist on any matched project (e.g. direct-won).
    const getLeadServiceQuotedPricesFromExpectedMargin = (lead) => {
        const leadProjects = getLeadProjectsForRevenue(lead);
        if (leadProjects.length === 0) return [];

        const serviceTypes = [
            ...new Set(
                leadProjects
                    .map((proj) => String(proj?.TypeService || "").trim())
                    .filter(Boolean)
            ),
        ];
        if (serviceTypes.length === 0) return [];

        const leadQuotedValue = Number(lead?.QuotedValue) || 0;
        if (!(leadQuotedValue > 0)) return [];

        const rowKey = getRevenueLeadRowKey(lead);
        const projectName = String(lead?.ProjectName || "").trim();

        // All matched projects (Quoted + Actual) so we can read Salesmargin from Quoted twin.
        const allMatched = (projects || []).filter((proj) => {
            const projectKey =
                proj?.quotationId != null && String(proj.quotationId).trim() !== ""
                    ? String(proj.quotationId).trim()
                    : String(proj?.ProjectName ?? "").trim();

            if (rowKey && projectKey && projectKey === rowKey) return true;
            if (
                projectName &&
                String(proj?.ProjectName ?? "").trim() === projectName
            ) {
                return true;
            }
            return false;
        });

        const readMargin = (p) =>
            Number(p?.Salesmargin ?? p?.salesmargin ?? p?.ProjectMargin ?? 0) || 0;

        const marginsByType = {};
        let sumMargins = 0;
        serviceTypes.forEach((type) => {
            const typeNorm = String(type).trim().toLowerCase();
            const candidates = allMatched.filter(
                (p) =>
                    String(p?.TypeService || "")
                        .trim()
                        .toLowerCase() === typeNorm
            );
            const quotedTwin = candidates.find(
                (p) => p?.isProjecttracking !== true && readMargin(p) > 0
            );
            const anyWithMargin = candidates.find((p) => readMargin(p) > 0);
            const margin = readMargin(
                quotedTwin || anyWithMargin || candidates[0]
            );
            marginsByType[type] = margin;
            sumMargins += margin;
        });

        if (sumMargins <= 0) {
            // No Expected Margin: weight by CostPerService (same idea as Quoted/Actual cost-share),
            // then last-resort equal split only if costs are also missing.
            const costsByType = {};
            let sumCosts = 0;
            serviceTypes.forEach((type) => {
                const typeNorm = String(type).trim().toLowerCase();
                const candidates = allMatched.filter(
                    (p) =>
                        String(p?.TypeService || "")
                            .trim()
                            .toLowerCase() === typeNorm
                );
                const cost = candidates.reduce((max, p) => {
                    const c = Number(p?.CostPerService ?? p?.costPerService ?? 0) || 0;
                    return c > max ? c : max;
                }, 0);
                costsByType[type] = cost;
                sumCosts += cost;
            });

            if (sumCosts > 0) {
                return serviceTypes.map((type) => ({
                    type,
                    amount: (costsByType[type] / sumCosts) * leadQuotedValue,
                }));
            }

            const equal = leadQuotedValue / serviceTypes.length;
            return serviceTypes.map((type) => ({ type, amount: equal }));
        }

        return serviceTypes.map((type) => ({
            type,
            amount: (marginsByType[type] / sumMargins) * leadQuotedValue,
        }));
    };

    const getLeadServiceQuotedPrices = (lead) => {
        // CRM path uses aggregateCrmRevenueFromLatestQuotations below (not per-lead here).
        if (isCrmPackage) {
            const fromLatestQuote = getLeadServicePricesFromLatestQuotation(
                quotations,
                lead
            );
            return fromLatestQuote && fromLatestQuote.length > 0
                ? fromLatestQuote
                : [];
        }

        // Non-CRM (ERP): same formula as Actual Itemized P&L — Expected Margin %.
        return getLeadServiceQuotedPricesFromExpectedMargin(lead);
    };

    const revenueTotals = REVENUE_SERVICE_CATEGORIES.reduce((acc, cat) => {
        acc[cat] = 0;
        return acc;
    }, {});

    // Breakdown of non-main service types that roll into Multiple
    const multipleBreakdownTotals = {};

    const addServiceAmountToRevenueTotals = (type, amount) => {
        const mainType = matchMainRevenueServiceType(type);
        const value = Number(amount) || 0;
        if (!(value > 0)) return;
        if (mainType) {
            revenueTotals[mainType] += value;
            return;
        }
        revenueTotals.Multiple += value;
        const otherType = String(type || "").trim() || "Other";
        multipleBreakdownTotals[otherType] =
            (Number(multipleBreakdownTotals[otherType]) || 0) + value;
    };

    if (isCrmPackage) {
        // CRM: sum Finance Summary prices from latest GeneratedQuote per lead only.
        // Independent of Top 10 Client / Won-only / equal-split QuotedValue.
        // Scope to dashboard-filtered leads when available; otherwise all quotations.
        const scopeLeads =
            Array.isArray(filteredReports) && filteredReports.length > 0
                ? filteredReports
                : Array.isArray(groupFilteredReports) && groupFilteredReports.length > 0
                    ? groupFilteredReports
                    : null;

        const crmServiceRows = aggregateCrmRevenueFromLatestQuotations({
            quotations,
            // If scope matching would exclude everything incorrectly, still try scoped first;
            // when scope is empty, count all quotations with Finance Summary prices.
            scopeLeads,
            selectedYear: selectedRevenueYear,
        });

        // If scoped aggregation found nothing but quotations exist, retry without lead-scope
        // (quotation leadid / lead.quotationId mismatches must not hide real quote prices).
        const crmRowsToUse =
            crmServiceRows.length > 0
                ? crmServiceRows
                : aggregateCrmRevenueFromLatestQuotations({
                    quotations,
                    scopeLeads: null,
                    selectedYear: selectedRevenueYear,
                });

        crmRowsToUse.forEach(({ type, amount }) => {
            addServiceAmountToRevenueTotals(type, amount);
        });
    } else {
        // ERP: only leads that appear on Actual P&L (salesperson Ongoing + managers submitted).
        const actualPlRevenueLeads = (revenueFilteredReports || []).filter((lead) =>
            shouldShowLeadOnActualPl({
                row: lead,
                projects,
                getRowKey: getRevenueLeadRowKey,
            })
        );

        actualPlRevenueLeads.forEach((lead) => {
            const serviceQuotedPrices = getLeadServiceQuotedPrices(lead);
            if (serviceQuotedPrices.length === 0) return;

            serviceQuotedPrices.forEach(({ type, amount }) => {
                addServiceAmountToRevenueTotals(type, amount);
            });
        });
    }

    // Round each service total to whole rupees (same as USAComma display) so
    // Total = exact sum of displayed service amounts (no 1₹ float drift).
    REVENUE_SERVICE_CATEGORIES.forEach((cat) => {
        revenueTotals[cat] = Math.round(Number(revenueTotals[cat]) || 0);
    });

    const multipleBreakdown = Object.entries(multipleBreakdownTotals)
        .map(([name, amount]) => ({
            name,
            amount: Math.round(Number(amount) || 0),
        }))
        .filter((entry) => entry.amount > 0)
        .sort((a, b) => b.amount - a.amount);

    // Keep Multiple amount equal to sum of its breakdown rows
    if (multipleBreakdown.length > 0) {
        revenueTotals.Multiple = multipleBreakdown.reduce(
            (sum, entry) => sum + Number(entry.amount || 0),
            0
        );
    }

    const revenueCategoriesToShow = revenueTotals.Multiple > 0
        ? REVENUE_SERVICE_CATEGORIES
        : MAIN_REVENUE_SERVICE_TYPES;

    const datarevenue = revenueCategoriesToShow.map((name) => ({
        name,
        originalValue: revenueTotals[name],
        originalIndex: REVENUE_SERVICE_CATEGORIES.indexOf(name),
        ...(name === "Multiple" ? { multipleBreakdown } : {}),
    }));
    const hasRevenueData = datarevenue.some((item) => Number(item.originalValue || 0) > 0);

    const sortedTopLeads = topLeads
        .slice()
        .sort((a, b) => (b.QuotedValue || 0) - (a.QuotedValue || 0));
    
    // Use adaptive transformation: keep large values at 0.98, compress smaller values more
    // This ensures the largest value stays perfect while smaller values are more compressed
    const maxValue = Math.max(...sortedTopLeads.map(lead => lead.QuotedValue || 0));
    const data = sortedTopLeads.map((lead, index) => {
        const originalValue = lead.QuotedValue || 0;
        if (originalValue <= 0) {
            return {
            name: lead.ClientName || `Lead ${index + 1}`,
                value: 0,
                originalValue: originalValue,
                originalIndex: index,
            };
        }
        
        // Calculate relative size (0 to 1)
        const relativeSize = originalValue / maxValue;
        
        // Use a proportional curve that preserves value differences clearly for ALL values
        // This ensures: smaller values have smaller bars, larger values have larger bars
        // All values show proportional differences according to their amounts
        // Very small (₹90,000): power 0.4-0.6 (preserves differences, still small)
        // Small-medium (₹1,00,000): power 0.6-0.8 (preserves differences, small)
        // Medium (₹3,63,000, ₹5,00,000, ₹8,00,000): power 0.8-0.95 (clear differences)
        // Large-medium (₹9,00,000, ₹10,00,000): power 0.95-0.98 (almost no compression, much bigger)
        // Largest: power 0.98 (no compression)
        let power;
        if (relativeSize > 0.08) {
            // Largest values (₹9,90,099, ₹10,00,000): power 0.999-1.0 (almost no compression - dominates chart)
            power = 0.999 + ((relativeSize - 0.08) / 0.92) * 0.001; // 0.999 to 1.0
        } else if (relativeSize > 0.05) {
            // Large-medium values: power 0.99-0.999 (₹8,00,000+ range - very large)
            power = 0.99 + ((relativeSize - 0.05) / 0.03) * 0.009; // 0.99 to 0.999
        } else if (relativeSize > 0.03) {
            // Medium values: power 0.9-0.99 (₹3,63,000, ₹5,00,000 range - clearly visible and large)
            power = 0.9 + ((relativeSize - 0.03) / 0.02) * 0.09; // 0.9 to 0.99
        } else if (relativeSize > 0.01) {
            // Small-medium values: power 0.4-0.9 (₹1,00,000 range - visible but much smaller)
            power = 0.4 + ((relativeSize - 0.01) / 0.02) * 0.5; // 0.4 to 0.9
        } else {
            // Very small values (₹90,000): power 0.08-0.4 (heavily compressed - tiny but visible)
            power = 0.08 + (relativeSize / 0.01) * 0.32; // 0.08 to 0.4
        }
        const transformedValue = Math.pow(originalValue, power);
        
        return {
            name: lead.ClientName || `Lead ${index + 1}`,
            value: transformedValue,
            originalValue: originalValue, // Keep original for tooltip
            originalIndex: index, // Store the index to find the exact lead
        };
    });
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const payloadData = payload[0].payload || payload[0];
            const dataIndex = payloadData.originalIndex;
            
            // Get the exact lead using the dataIndex from the sorted array
            const exactLead = dataIndex !== undefined && sortedTopLeads[dataIndex] ? sortedTopLeads[dataIndex] : null;
            
            if (!exactLead) {
                // Fallback: try to find by name and value
            const { name, value } = payload[0];
                const fallbackLead = sortedTopLeads.find(lead => lead.ClientName === name && lead.QuotedValue === value);
                if (!fallbackLead) return null;
                
                const colorIndex = sortedTopLeads.findIndex(l => l === fallbackLead);
                const bgColor = COLORS[colorIndex % COLORS.length] || "#1d72f3";
                
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                            maxWidth: "300px",
                    }}
                >
                        <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                        <strong>{name}</strong>
                    </p>
                        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                            <div 
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: `${bgColor}15`,
                                    borderLeft: `3px solid ${bgColor}`,
                                }}
                            >
                                <span style={{ fontSize: "12px", color: "#374151" }}>
                                    {fallbackLead.ClientName}
                                </span>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                    {currency}{USAComma(fallbackLead.QuotedValue)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            }
            
            const { name } = payload[0];
            const colorIndex = dataIndex;
            const bgColor = COLORS[colorIndex % COLORS.length] || "#1d72f3";
            
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                        maxWidth: "300px",
                    }}
                >
                    <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                        <strong>{name}</strong>
                    </p>
                    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                        <div 
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                backgroundColor: `${bgColor}15`,
                                borderLeft: `3px solid ${bgColor}`,
                            }}
                        >
                            <span style={{ fontSize: "12px", color: "#374151" }}>
                                {exactLead.ClientName}
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                {currency}{USAComma(exactLead.QuotedValue)}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

 

    const CustomTooltiprevenue = ({ active, payload }) => {
        if (!(active && payload && payload.length && isRevenueChartHovered)) return null;

        const payloadData = payload[0].payload || payload[0];
        const categoryName = payloadData.name || "";
        const amount = Number(payloadData.originalValue) || 0;
        const colorIndex =
            payloadData.originalIndex !== undefined
                ? payloadData.originalIndex
                : REVENUE_SERVICE_CATEGORIES.indexOf(categoryName);
        const bgColor = COLORSrevenue[colorIndex % COLORSrevenue.length] || "#1d72f3";

        // Portal to body; keep tooltip close to the hovered chart area
        const gap = 8;
        const approxWidth = 280;
        const approxHeight = 96;
        const mx = revenueMousePos.current.x;
        const my = revenueMousePos.current.y;
        const chartRect = revenueChartRef.current?.getBoundingClientRect?.();

        let left = mx + gap;
        let top = my + gap;

        if (chartRect) {
            const chartMidY = chartRect.top + chartRect.height * 0.45;
            // Bottom / lower segments: place tooltip just above the cursor (toward chart center)
            if (my >= chartMidY) {
                top = my - approxHeight - gap;
            } else {
                top = my + gap;
            }

            // Prefer sitting beside the cursor, still near the pie
            if (left + approxWidth > chartRect.right + 24) {
                left = mx - approxWidth - gap;
            }

            // Soft clamp so it stays visually near the graph (not far below/above the card)
            const minTop = chartRect.top - 4;
            const maxTop = chartRect.bottom - approxHeight * 0.35;
            top = Math.min(Math.max(top, minTop), maxTop);

            const minLeft = Math.max(8, chartRect.left - 8);
            const maxLeft = Math.min(
                (typeof window !== "undefined" ? window.innerWidth : chartRect.right) - approxWidth - 8,
                chartRect.right - 40
            );
            left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
        } else if (typeof window !== "undefined") {
            if (left + approxWidth > window.innerWidth - 8) {
                left = mx - approxWidth - gap;
            }
            if (top + approxHeight > window.innerHeight - 8) {
                top = my - approxHeight - gap;
            }
            left = Math.max(8, left);
            top = Math.max(8, top);
        }

        return createPortal(
            <div
                style={{
                    position: "fixed",
                    left,
                    top,
                    zIndex: 99999,
                    pointerEvents: "none",
                    background: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.18)",
                    maxWidth: "360px",
                }}
            >
                <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                    <strong>{categoryName}</strong>
                </p>
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "8px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            backgroundColor: `${bgColor}15`,
                            borderLeft: `3px solid ${bgColor}`,
                            minWidth: "220px",
                        }}
                    >
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#374151",
                                lineHeight: "1.4",
                            }}
                        >
                            Revenue
                        </span>
                        <span
                            style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#111827",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {currency}
                            {USAComma(amount)}
                        </span>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    // const highestQuotedLead = topLeads.reduce((max, lead) => {
    //     return lead.QuotedValue > (max?.QuotedValue || 0) ? lead : max;
    // }, null);

    // const highestQuotedGroup = groupedData.reduce((max, item) => {
    //     return item.totalQuotedValue > max.totalQuotedValue ? item : max;
    // }, groupedData[0]);

    return (
        <div className="relative z-20 flex h-full min-h-0 w-full max-w-full gap-2 overflow-visible sm:gap-3">
            <div
                className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white px-2 sm:px-3"
                style={{
                    fontFamily: "Lora",
                    fontWeight: 600,
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px"
                }}
            >
                <div className="flex h-full min-h-0 flex-col">
                    <div className="shrink-0 pt-0.5">
                        <div className="w-full flex flex-wrap md:flex-nowrap justify-between items-center">
                            <div className="w-full md:w-[70%]">
                                <h1 className="text-md font-semibold ">Top 10 Client</h1>
                            </div>
                            <div className="w-full md:w-[30%] flex items-center justify-end mt-2 md:mt-0">
                                <div className="show-potential-download lg:justify-end">
                                    <div className=" flex cursor-pointer items-center justify-between text-3xl sm:text-xl md:text-md">
                                        {probabilityTab === "table" && (
                                            <span title="Graph" className="rounded-full bg-[#3072f0] p-1">
                                                <ChartColumnDecreasing size={20} color="#ffffff"
                                                    onClick={() => setProbabilityTab("graph")}
                                                />
                                            </span>
                                        )}
                                        {probabilityTab === "graph" && (
                                            <span title="Table" className="rounded-full bg-[#3072f0] p-1 rotate-180">
                                                <TableOfContents size={20} color="#ffffff" onClick={() => setProbabilityTab("table")} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="show-potential-download lg:justify-end ">
                                    <div className=" inline-block relative px-3 mt-2">
                                        <div
                                            ref={dropdownRefclient}
                                            className={`flex items-center text-4xl sm:text-md md:text-xl lg:text-xl xl:text-2xl 2xl:text-4xl ${topLeads.length === 0
                                                ? "opacity-50 pointer-events-none"
                                                : ""
                                                } ${isDownloadViewOnly ? "opacity-60 pointer-events-none cursor-not-allowed" : ""}`}
                                        >
                                            <Download color={isDownloadViewOnly ? "#9ca3af" : "#0a0a0a"}
                                                onClick={toggleDropdown}
                                                className={isDownloadViewOnly ? "cursor-not-allowed" : "cursor-pointer"} />

                                            {isDropdownVisible && (
                                                <div className="absolute top-8 -left-0 shadow-lg z-50 bg-white text-base">
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "pdf" } })
                                                        }
                                                    >
                                                        PDF
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "excel" } })
                                                        }
                                                    >
                                                        Excel
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "word" } })
                                                        }
                                                    >
                                                        Word
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 flex lg:pt-3 xl:pt-3">
                        {probabilityTab === "table" ? (
                            topLeads.length > 0 ? (
                                <div className="w-full max-h-[9.5rem] overflow-auto custom-scroll border">
                                    <div className="w-full bg-[#E8E8E8] text-black text-xs font-bold flex sticky top-0">
                                        <div className="w-[50%] text-start px-1 py-1 border-r border-gray-400">
                                            <p>Client</p>
                                        </div>
                                        <div className="w-[50%] text-start px-1 py-1">
                                            <p>Amount ({currency})</p>
                                        </div>
                                    </div>

                                    <div className=" text-xs font-normal">
                                        {topLeads
                                            ?.slice() // clone so original state isn't mutated
                                            .sort((a, b) => b.QuotedValue - a.QuotedValue) // highest → lowest
                                            .map((item, index) => (<div className="w-full flex" key={index}>
                                                <div className="w-[50%] text-start border-b border-gray-300 border-r border-gray-400 px-1 py-1 break-words whitespace-normal">
                                                    {item.ClientName}
                                                </div>
                                                <div className="w-[50%] text-start  border-b border-gray-300 px-1 py-1">
                                                    {USAComma(item.QuotedValue)}
                                                </div>
                                            </div>
                                            ))}
                                    </div>
                                    <div className="w-full flex bg-[#E8E8E8] font-bold text-xs">
                                        <div className="w-[50%] text-start  px-1 py-1 border-r border-gray-400">
                                            <p>Total</p>
                                        </div>
                                        <div className="w-[50%]  text-start truncate  px-1 py-1">
                                            <p className="truncate">
                                                {USAComma(Number(topLeads
                                                    .reduce(
                                                        (total, item) =>
                                                            total + Number(item.QuotedValue || 0),
                                                        0
                                                    )))}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p
                                            className={`py-0.5 text-[0.6rem] ${isDownloadViewOnly ? 'text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60' : 'text-[#0070c0] cursor-pointer'}`}
                                            onClick={AllClient}
                                        >
                                            See all*
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                    <p className="noDataAvailable">
                                        No data available
                                    </p>{" "}
                                </div>
                            )
                        ) : topLeads.length > 0 ? (
                            <div className="w-full min-h-0 flex flex-1 flex-col justify-center">
                                <div className="w-full min-h-0 flex flex-1 items-center justify-between gap-8 py-1">
                                    <div className="relative z-10 flex-shrink-0 w-[min(480px,56%)] max-h-full p-1 flex flex-col items-center justify-center">
                                        <div className="relative z-20 mb-1 text-center">
                                            <div className="text-base font-bold text-[#1F2937] leading-none">10</div>
                                            <div className="mt-0.5 text-[12px] font-medium text-gray-400">Clients</div>
                                        </div>
                                        <div className="relative z-10 w-full max-h-full aspect-square overflow-hidden">
                                        {(() => {
                                            const total = (data || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                            const pieRows = (data || []).map((r) => ({
                                                ...r,
                                                value: (Number(r?.originalValue || 0) / total) * 100,
                                            }));
                                            return (
                                                <>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={pieRows}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius="50%"
                                                                outerRadius="99%"
                                                                fill="#8884d8"
                                                                dataKey="value"
                                                                minAngle={5}
                                                                paddingAngle={0.1}
                                                            >
                                                                {pieRows.map((entry, index) => (
                                                                    <Cell
                                                                        key={`cell-${index}`}
                                                                        fill={COLORS[index % COLORS.length]}
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip content={<CustomTooltip />} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    </div>

                                    {(() => {
                                        const total = (data || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                        const rows = (data || [])
                                            .map((r) => ({ ...r, pct: (Number(r?.originalValue || 0) / total) * 100 }))
                                            .sort((a, b) => (b.pct || 0) - (a.pct || 0));
                                        const topN = rows.slice(0, 4);
                                        const otherCount = Math.max(rows.length - topN.length, 0);
                                        const topSum = topN.reduce((t, r) => t + Number(r?.pct || 0), 0);
                                        const otherPct = Math.max(0, Math.round(100 - topSum));
                                        return (
                                            <div className="flex-1 min-w-0 pr-1">
                                                <div className="flex flex-col gap-3">
                                                    {topN.map((row, idx) => (
                                                        <div key={idx} className="flex w-full items-start gap-3 text-[12px] text-[#1F2937]">
                                                            <div className="flex flex-1 min-w-0 items-start gap-2">
                                                                <span
                                                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                                                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                                                />
                                                                <span className="min-w-0 w-0 max-w-full flex-1 whitespace-normal break-words leading-snug">
                                                                    {row?.name || `Client ${idx + 1}`}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {otherCount > 0 && (
                                                        <div className="text-[11px] text-gray-400 whitespace-nowrap">
                                                            + {otherCount} others — {otherPct}%
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                <p className="noDataAvailable">
                                    No data available
                                </p>{" "}
                            </div>
                        )}
                    </div>
                </div>
            </div>


            <div
                className="relative z-20 flex min-h-0 min-w-0 flex-1 flex-col overflow-visible rounded-lg bg-white px-2 sm:px-3"
                style={{
                    fontFamily: "Lora",
                    fontWeight: 600,
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px"
                }}
            >
                <div className="flex h-full min-h-0 flex-col overflow-visible">
                    <div className="relative z-[70] shrink-0 pt-1">
                        <div className="flex flex-wrap justify-start items-center gap-2 xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
                            <div className="flex min-w-0 flex-1 items-center gap-2 md:max-w-[calc(100%-5.5rem)]">
                                <h1 className="text-md font-semibold shrink-0">Revenue</h1>
                                <div className="shrink-0">
                                    <select
                                        value={selectedRevenueYear}
                                        onChange={(e) => setSelectedRevenueYear(e.target.value)}
                                        className="h-7 w-[4.5rem] max-w-[4.5rem] rounded-md border border-gray-300 bg-white px-1 text-[11px] font-semibold text-[#1F2937] outline-none focus:border-[#3072f0]"
                                        title={selectedRevenueYear}
                                    >
                                        <option value="All Years">All Years</option>
                                        {revenueYearOptions.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end">
                                <div className="show-potential-download lg:justify-end">
                                    <div className=" flex cursor-pointer items-center justify-between text-3xl sm:text-xl md:text-md">
                                        {probabilityTabRevenue === "table" && (
                                            <span title="Graph" className="rounded-full bg-[#3072f0] p-1">
                                                {/* <SignalCellularAltIcon
                                                    className="cursor-pointer"
                                                    onClick={() => setProbabilityTabRevenue("graph")}
                                                    fontSize="3rem"
                                                /> */}
                                                <ChartColumnDecreasing size={20} color="#ffffff"
                                                    onClick={() => setProbabilityTabRevenue("graph")}
                                                />
                                            </span>
                                        )}
                                        {probabilityTabRevenue === "graph" && (
                                            <span title="Table" className="rounded-full bg-[#3072f0] p-1 rotate-180">
                                                {/* <TableChartIcon
                                                    className="cursor-pointer"
                                                    onClick={() => setProbabilityTabRevenue("table")}
                                                    fontSize="3rem"
                                                /> */}
                                                <TableOfContents size={20}
                                                    color="#ffffff"
                                                    onClick={() => setProbabilityTabRevenue("table")} />

                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="show-potential-download lg:justify-end ">
                                    <div className=" inline-block relative px-3 mt-2">
                                        <div
                                            ref={popupRef}
                                            className={`flex items-center text-4xl sm:text-md md:text-xl lg:text-xl xl:text-2xl 2xl:text-4xl ${!hasRevenueData
                                                ? "opacity-50 pointer-events-none"
                                                : ""
                                                } ${isDownloadViewOnly ? "opacity-60 pointer-events-none cursor-not-allowed" : ""}`}
                                        >

                                            <Download color={isDownloadViewOnly ? "#9ca3af" : "#0a0a0a"}
                                                onClick={toggleDropdownRevenue}
                                                className={isDownloadViewOnly ? "cursor-not-allowed" : "cursor-pointer"} />

                                            {isDropdownVisibleRevenue && (
                                                <div className="absolute top-8 left-0 z-[80] min-w-[5rem] border border-gray-200 bg-white text-base shadow-lg">
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "pdf" },
                                                            })
                                                        }
                                                    >
                                                        PDF
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "excel" },
                                                            })
                                                        }
                                                    >
                                                        Excel
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "word" },
                                                            })
                                                        }
                                                    >
                                                        Word
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="relative z-30 min-h-0 flex-1 flex overflow-visible lg:pt-3 xl:pt-3">
                        {probabilityTabRevenue === "table" ? (
                            datarevenue.some((item) => Number(item.originalValue || 0) > 0) ? (
                                <div className="w-full max-h-[9.5rem] overflow-auto custom-scroll border">
                                        <div className="w-full bg-[#E8E8E8] text-black text-xs font-bold flex sticky top-0 z-[199]">
                                            <div className="w-[50%] text-start px-1 py-1 border-r border-gray-400">
                                                <p>Service Type</p>
                                            </div>
                                            <div className="w-[50%] text-start px-1 py-1">
                                                <p>Amount ({currency})</p>
                                            </div>
                                        </div>

                                        <div className="">
                                            {[...datarevenue]
                                                .filter((item) => Number(item.originalValue || 0) > 0)
                                                .sort((a, b) => Number(b.originalValue || 0) - Number(a.originalValue || 0))
                                                .map((item, index) => {
                                                    const isMultiple = item.name === "Multiple";
                                                    const breakdown = Array.isArray(item.multipleBreakdown)
                                                        ? item.multipleBreakdown
                                                        : [];

                                                    return (
                                                        <div className="w-full flex text-xs font-normal" key={index}>
                                                            <div
                                                                className="w-[50%] cursor-default border-gray-300 text-start border-b border-r border-gray-400 px-1 py-1 relative group"
                                                            >
                                                                <span className="block truncate">{item.name}</span>
                                                                {isMultiple && breakdown.length > 0 && (
                                                                    <span
                                                                        className="pointer-events-none absolute left-1 top-full z-[200] mt-0.5 hidden max-w-[260px] whitespace-normal break-words rounded bg-black px-2 py-1 text-[11px] font-medium leading-snug text-white shadow-lg group-hover:block"
                                                                        role="tooltip"
                                                                    >
                                                                        {breakdown.map((entry) => (
                                                                            <span key={entry.name} className="block">
                                                                                {`${entry.name} - ${USAComma(entry.amount)}`}
                                                                            </span>
                                                                        ))}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="w-[50%] text-start  border-gray-300 border-b px-1 py-1">
                                                                {USAComma(item.originalValue)}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        <div className="w-full bg-[#E8E8E8] flex font-bold text-xs">
                                            <div className="w-[50%] text-start  px-1 py-1 border-r border-gray-400">
                                                <p>Total</p>
                                            </div>
                                            <div className="w-[50%] text-start  truncate  px-1 py-1">
                                                <p>
                                                    {USAComma(
                                                        Number(
                                                            datarevenue.reduce(
                                                                (total, item) => total + Number(item.originalValue || 0),
                                                                0
                                                            )
                                                        )
                                                    )}
                                                </p>
                                            </div>


                                        </div>

                                        <div>
                                            <p
                                                className={`py-0.5 text-[0.6rem] ${isDownloadViewOnly ? 'text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60' : 'text-[#0070c0] cursor-pointer'}`}
                                                onClick={AllClientServiceType}
                                            >
                                                See all*
                                            </p>
                                        </div>

                                </div>
                            ) : (
                                <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                    <p className="noDataAvailable">
                                        No data available
                                    </p>{" "}
                                </div>
                            )
                        ) : hasRevenueData ? (
                            <div className="w-full min-h-0 flex flex-1 flex-col justify-center overflow-visible">
                                <div className="w-full min-h-0 flex flex-1 items-center justify-between gap-8 py-1 overflow-visible">
                                    <div className="relative z-50 flex-shrink-0 w-[min(480px,56%)] max-h-full p-1 flex flex-col items-center justify-center overflow-visible">
                                        {(() => {
                                            const total = (datarevenue || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                            const pieRows = (datarevenue || [])
                                                .filter((r) => Number(r?.originalValue || 0) > 0)
                                                .map((r) => ({
                                                    ...r,
                                                    value: (Number(r?.originalValue || 0) / total) * 100,
                                                }));
                                            const totalText = `${currency}${USAComma(
                                                (datarevenue || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0)
                                            )}`;
                                            return (
                                                <>
                                                    <div className="relative z-10 mb-1 text-center">
                                                        <div className="text-sm font-semibold text-[#1F2937] leading-none">
                                                            {totalText}
                                                        </div>
                                                        <div className="mt-0.5 text-[12px] font-medium text-gray-400">Total</div>
                                                    </div>
                                                    <div
                                                        ref={revenueChartRef}
                                                        className="relative z-[60] w-full max-h-full aspect-square overflow-visible"
                                                        onMouseEnter={() => setIsRevenueChartHovered(true)}
                                                        onMouseLeave={() => setIsRevenueChartHovered(false)}
                                                        onMouseMove={(e) => {
                                                            revenueMousePos.current = {
                                                                x: e.clientX,
                                                                y: e.clientY,
                                                            };
                                                        }}
                                                    >
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={pieRows}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius="50%"
                                                                outerRadius="99%"
                                                                fill="#8884d8"
                                                                dataKey="value"
                                                                minAngle={5}
                                                                paddingAngle={0.1}
                                                            >
                                                                {pieRows.map((entry, index) => (
                                                                    <Cell
                                                                        key={`cell-${index}`}
                                                                        fill={COLORSrevenue[entry.originalIndex % COLORSrevenue.length]}
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip
                                                                content={<CustomTooltiprevenue />}
                                                                wrapperStyle={{
                                                                    visibility: "hidden",
                                                                    pointerEvents: "none",
                                                                }}
                                                                allowEscapeViewBox={{ x: true, y: true }}
                                                            />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    {(() => {
                                        return (
                                            <div className="relative z-20 flex-1 min-w-0 overflow-visible pr-1">
                                                <div className="flex flex-col gap-3 overflow-visible">
                                                    {(datarevenue || [])
                                                        .filter((row) => Number(row?.originalValue || 0) > 0)
                                                        .map((row) => {
                                                        const isMultiple = row.name === "Multiple";
                                                        const breakdown = Array.isArray(row.multipleBreakdown)
                                                            ? row.multipleBreakdown
                                                            : [];

                                                        return (
                                                        <div key={row.name} className="relative flex w-full items-start gap-3 text-[12px] text-[#1F2937]">
                                                            <div className="flex flex-1 min-w-0 items-start gap-2">
                                                                <span
                                                                    className="h-2.5 w-2.5 rounded-full shrink-0 mt-0.5"
                                                                    style={{ backgroundColor: COLORSrevenue[row.originalIndex % COLORSrevenue.length] }}
                                                                />
                                                                <span className="relative group min-w-0 flex-1 cursor-default truncate leading-snug">
                                                                    {row.name}
                                                                    {isMultiple && breakdown.length > 0 && (
                                                                        <span
                                                                            className="pointer-events-none absolute left-0 top-full mt-1 z-[100] hidden max-w-[260px] whitespace-normal break-words rounded bg-black px-2 py-1 text-[11px] font-medium leading-snug text-white shadow-lg group-hover:block"
                                                                            role="tooltip"
                                                                        >
                                                                            {breakdown.map((entry) => (
                                                                                <span key={entry.name} className="block">
                                                                                    {`${entry.name} - ${USAComma(entry.amount)}`}
                                                                                </span>
                                                                            ))}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                <p className="noDataAvailable">
                                    No data available
                                </p>{" "}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>)
}

export default Sales